import React from 'react';

function renderRateType(item) {
  if (item.type) return item.type;
  const rt = item.rate_type;
  if (rt === 'hour') return '/hr';
  if (rt === 'day') return '/day';
  if (rt === 'night') return '/night';
  if (rt === 'hole') return '/hole';
  if (rt === 'linear_footage') return '/LF';
  return 'fixed';
}

function ItemRow({ item, idx }) {
  const service = item.service || item.description || 'Service';
  const customDesc = item.custom_description || '';
  const descField = item.description || '';
  const description = customDesc || (descField && descField !== service ? descField : '');
  const type = renderRateType(item);
  const qty = item.qty || item.quantity || 1;
  const price = item.price || item.unit_price || 0;
  const itemTotal = item.total || (qty * price);

  return (
    <tr key={item.id || idx} className="border-b border-slate-100">
      <td className="py-1.5 px-2 font-medium text-slate-800 text-[11px]">{service}</td>
      <td className="py-1.5 px-2 text-slate-600 text-[11px]">{description}</td>
      <td className="py-1.5 px-2 text-center text-slate-700 text-[11px]">{qty}</td>
      <td className="py-1.5 px-2 text-center text-slate-500 text-[11px]">{type}</td>
      <td className="py-1.5 px-2 text-right text-slate-700 text-[11px]">${price.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
      <td className="py-1.5 px-2 text-right font-medium text-slate-800 text-[11px]">
        ${itemTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}
      </td>
    </tr>
  );
}

function TableHeader() {
  const thClass = "py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase bg-slate-100";
  return (
    <thead>
      <tr>
        <th className={`text-left ${thClass}`}>Service</th>
        <th className={`text-left ${thClass}`}>Description</th>
        <th className={`text-center ${thClass} w-12`}>Qty</th>
        <th className={`text-center ${thClass} w-14`}>Type</th>
        <th className={`text-right ${thClass} w-20`}>Rate</th>
        <th className={`text-right ${thClass} w-20`}>Amount</th>
      </tr>
    </thead>
  );
}

function ScopeSection({ scope, scopeIndex }) {
  const items = (scope.items || []).map((item, idx) => ({ ...item, id: `scope-${scopeIndex}-${idx}` }));
  const subtotal = items.reduce((sum, item) => sum + (item.total || (item.quantity || 1) * (item.unit_price || 0)), 0);

  return (
    <div className="mb-4" data-testid={`print-scope-${scopeIndex}`}>
      <p className="text-[11px] font-bold text-slate-800 mb-1 uppercase tracking-wide border-b border-slate-300 pb-1">
        {scope.name || `Scope ${scopeIndex + 1}`}
      </p>
      <table className="w-full text-xs border-collapse">
        <TableHeader />
        <tbody>
          {items.map((item, idx) => (
            <ItemRow key={item.id} item={item} idx={idx} />
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5} className="py-1.5 px-2 text-right font-semibold text-slate-700 text-[11px] bg-slate-50">
              {scope.name || 'Scope'} Subtotal
            </td>
            <td className="py-1.5 px-2 text-right font-semibold text-slate-700 text-[11px] bg-slate-50">
              ${subtotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default function ProposalLineItems({ standaloneItems, scopes, grandTotal }) {
  const hasScopes = scopes && scopes.length > 0;
  const hasStandalone = standaloneItems && standaloneItems.length > 0;

  return (
    <div className="mb-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Services</p>

      {/* Render each scope section */}
      {hasScopes && scopes.map((scope, idx) => (
        <ScopeSection key={idx} scope={scope} scopeIndex={idx} />
      ))}

      {/* Render standalone items (not in any scope) */}
      {hasStandalone && (
        <div className={hasScopes ? "mb-4" : ""}>
          {hasScopes && (
            <p className="text-[11px] font-bold text-slate-800 mb-1 uppercase tracking-wide border-b border-slate-300 pb-1">
              Additional Items
            </p>
          )}
          <table className="w-full text-xs border-collapse">
            <TableHeader />
            <tbody>
              {standaloneItems.map((item, idx) => (
                <ItemRow key={item.id || idx} item={item} idx={idx} />
              ))}
            </tbody>
            {/* If no scopes, show total in the standalone table footer */}
            {!hasScopes && (
              <tfoot>
                <tr>
                  <td colSpan={5} className="py-2 px-2 text-right font-bold text-slate-900 text-xs uppercase bg-slate-50">Total</td>
                  <td className="py-2 px-2 text-right font-bold text-slate-900 text-sm bg-slate-50">${grandTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Grand Total bar when scopes are present */}
      {hasScopes && (
        <div className="mt-2 border-t-2 border-slate-800 pt-2">
          <div className="flex justify-end">
            <table className="text-xs">
              <tbody>
                <tr>
                  <td className="py-1 px-3 text-right font-bold text-slate-900 text-sm uppercase">Grand Total</td>
                  <td className="py-1 px-3 text-right font-bold text-slate-900 text-base">
                    ${grandTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
